import React, { createContext, useContext, useState, useEffect } from "react";

const LeaveContext = createContext(null);

const INITIAL_BALANCES = [
  { employeeId: "EMP001", name: "Alice Dubois", department: "Développement Mobile", totalDays: 30, usedDays: 12, remainingDays: 18 },
  { employeeId: "EMP002", name: "Marc Lavoine", department: "Ingénierie Logicielle", totalDays: 30, usedDays: 10, remainingDays: 20 },
  { employeeId: "EMP003", name: "Julie Robert", department: "Ingénierie Logicielle", totalDays: 30, usedDays: 5, remainingDays: 25 },
  { employeeId: "EMP004", name: "Luc Besson", department: "Développement Mobile", totalDays: 30, usedDays: 8, remainingDays: 22 },
];

const INITIAL_REQUESTS = [
  {
    id: "REQ001",
    employeeId: "EMP001",
    employeeName: "Alice Dubois",
    department: "Développement Mobile",
    startDate: "2026-05-10",
    endDate: "2026-05-15",
    type: "Annuel",
    reason: "Vacances de printemps",
    status: "Approuvé",
    daysCount: 6,
  },
  {
    id: "REQ002",
    employeeId: "EMP001",
    employeeName: "Alice Dubois",
    department: "Développement Mobile",
    startDate: "2026-07-20",
    endDate: "2026-07-24",
    type: "RTT",
    reason: "Besoin de repos",
    status: "En attente Manager",
    daysCount: 5,
  },
  {
    id: "REQ003",
    employeeId: "EMP002",
    employeeName: "Marc Lavoine",
    department: "Ingénierie Logicielle",
    startDate: "2026-07-12",
    endDate: "2026-07-15",
    type: "Maladie",
    reason: "Grippe",
    status: "Validé Manager (En attente RH)",
    daysCount: 4,
  },
  {
    id: "REQ004",
    employeeId: "EMP003",
    employeeName: "Julie Robert",
    department: "Ingénierie Logicielle",
    startDate: "2026-08-01",
    endDate: "2026-08-07",
    type: "Annuel",
    reason: "Voyage d'été",
    status: "En attente Manager",
    daysCount: 7,
  },
];

const INITIAL_RECRUITMENT = [
  {
    id: "REC001",
    title: "Développeur Senior React (F/H)",
    department: "Ingénierie",
    location: "Paris (Hybride)",
    candidates: [
      { name: "Damien Vart", score: 92, rank: 1, status: "Recommandé", cvLink: "#" },
      { name: "Léa Roussel", score: 85, rank: 2, status: "Intéressant", cvLink: "#" },
      { name: "Hugo Bernard", score: 64, rank: 3, status: "A revoir", cvLink: "#" },
    ]
  },
  {
    id: "REC002",
    title: "Manager RH / Head of People (F/H)",
    department: "Ressources Humaines",
    location: "Lyon (Temps plein)",
    candidates: [
      { name: "Clara Monnet", score: 95, rank: 1, status: "Recommandé", cvLink: "#" },
      { name: "Antoine Giraud", score: 78, rank: 2, status: "Intéressant", cvLink: "#" },
    ]
  }
];

export const LeaveProvider = ({ children }) => {
  const [requests, setRequests] = useState(() => {
    const savedRequests = localStorage.getItem("smarthr_requests");
    return savedRequests ? JSON.parse(savedRequests) : INITIAL_REQUESTS;
  });

  const [balances, setBalances] = useState(() => {
    const savedBalances = localStorage.getItem("smarthr_balances");
    return savedBalances ? JSON.parse(savedBalances) : INITIAL_BALANCES;
  });

  const [recruitment, setRecruitment] = useState(() => {
    const savedRecruitment = localStorage.getItem("smarthr_recruitment");
    return savedRecruitment ? JSON.parse(savedRecruitment) : INITIAL_RECRUITMENT;
  });

  useEffect(() => {
    localStorage.setItem("smarthr_requests", JSON.stringify(requests));
  }, [requests]);

  useEffect(() => {
    localStorage.setItem("smarthr_balances", JSON.stringify(balances));
  }, [balances]);

  useEffect(() => {
    localStorage.setItem("smarthr_recruitment", JSON.stringify(recruitment));
  }, [recruitment]);

  const addLeaveRequest = (newRequest) => {
    const requestWithId = {
      ...newRequest,
      id: "REQ" + (requests.length + 1).toString().padStart(3, "0"),
      status: "En attente Manager",
    };
    setRequests((prev) => [requestWithId, ...prev]);
  };

  const updateRequestStatus = (requestId, newStatus) => {
    setRequests((prevRequests) =>
      prevRequests.map((req) => {
        if (req.id !== requestId) return req;

        // If RH approves the request, update the employee balance
        if (newStatus === "Approuvé" && req.status !== "Approuvé") {
          setBalances((prevBalances) =>
            prevBalances.map((bal) => {
              if (bal.employeeId !== req.employeeId) return bal;
              const updatedUsed = bal.usedDays + req.daysCount;
              const updatedRemaining = Math.max(0, bal.totalDays - updatedUsed);
              return {
                ...bal,
                usedDays: updatedUsed,
                remainingDays: updatedRemaining,
              };
            })
          );
        }
        
        return { ...req, status: newStatus };
      })
    );
  };

  return (
    <LeaveContext.Provider
      value={{
        requests,
        balances,
        recruitment,
        addLeaveRequest,
        updateRequestStatus,
      }}
    >
      {children}
    </LeaveContext.Provider>
  );
};

export const useLeave = () => {
  const context = useContext(LeaveContext);
  if (!context) {
    throw new Error("useLeave must be used within a LeaveProvider");
  }
  return context;
};
